import React, {useEffect} from 'react';
import {Link, Redirect, useParams} from 'react-router-dom';
import GoogleButton from 'shared/components/google-button';
import Separator from 'shared/components/separator';
import GoogleOAuth from 'shared/modules/google-oauth';
import FormButton from 'src/components/button';
import FormikCredentialsInput, {
  genericSignupFormSchema,
} from 'src/components/inputs/formik-credentials-input/formik-credentials-input';
import {ADULT_LEARNER} from '../../utils';
import {Form, Formik} from 'formik';
import {BasicSignUpProps, indefiniteArticle, usertypeData} from '../utils';

interface UserSignUpProps extends BasicSignUpProps {
  onSubmit: (formValues: {identifier: string; password: string}) => void;
}

const UserSignUp = ({size, googleSignUp, onSubmit, setNavigationAnnouncement}: UserSignUpProps) => {
  const {userType, userSubtype} = useParams<{userType: string; userSubtype?: string}>();

  useEffect(() => {
    setNavigationAnnouncement(`${usertypeData(userType, userSubtype).text} Sign Up`);
    GoogleOAuth.init();
  }, [userType, userSubtype, setNavigationAnnouncement]);

  const {icon: Icon, text} = usertypeData(userType, userSubtype);

  // Redirect for adult learners if the URL structure is incorrect
  if (userType === ADULT_LEARNER && location.pathname.slice(1).split('/').length < 4) {
    return <Redirect to="/signup" />;
  }

  return (
    <>
      <div className="auth-signup-user-icon">
        <Icon width={size} height={size} />
      </div>
      <h1 className="auth-signup-user-header">
        Sign Up as {indefiniteArticle(text)} {text}
      </h1>
      <Formik
        initialValues={{
          identifier: '',
          password: '',
        }}
        onSubmit={async (values, formik) => {
          const formData = {
            identifier: values.identifier,
            password: values.password,
          };

          try {
            await onSubmit(formData);
          } catch (errors: $TSFixMe) {
            Object.keys(errors).forEach((field) => {
              if (errors[field]) {
                formik.setFieldError(field, errors[field]);
              }
            });
          }
        }}
        validationSchema={genericSignupFormSchema}
      >
        {(formikProps) => (
          <Form>
            <FormikCredentialsInput
              signUp
              identifierHeader={userSubtype ? 'Official Work Email' : 'Email'}
            />
            <p className="auth-signup-disclaimer">
              By agreeing and continuing, you acknowledge and agree to the{' '}
              <Link to="/terms_of_use" className="auth-shared-link caption">
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="auth-shared-link caption">
                Privacy and Cookie Policy
              </Link>
              .
            </p>
            <FormButton
              size="large"
              type="submit"
              className="auth-shared-submit-button"
              cy="signup"
              width="100%"
              disabled={formikProps.isSubmitting || !formikProps.isValid}
            >
              {formikProps.isSubmitting ? 'Signing Up...' : 'Agree & Continue'}
            </FormButton>
            <Separator>Or</Separator>
            <GoogleButton
              size="medium"
              buttonName="auth-login-google"
              className="auth-shared-google-button"
              onClick={googleSignUp}
            >
              Agree & Sign Up with Google
            </GoogleButton>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default UserSignUp;
